import { Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { HelpLabel } from '@/features/guided-help';
import { cn } from '@/lib/utils';
import type { TournamentConfigFieldConfig } from './types';
import { DEFAULT_TOURNAMENT_CONFIG_FIELD_CONFIG } from './types';
import {
  wizardBlockCardClassName,
  wizardBlockShellClassName,
} from '../wizardBlockChrome';

const DEFAULT_PRESETS = [30, 45, 60, 75, 90];

export interface MatchDurationFieldProps {
  value: number;
  onChange: (minutes: number) => void;
  config?: TournamentConfigFieldConfig;
  /** Override default [30, 45, 60, 75, 90] */
  presets?: number[];
  wrapInCard?: boolean;
  inputId?: string;
}

export function MatchDurationField({
  value,
  onChange,
  config: userConfig,
  presets = DEFAULT_PRESETS,
  wrapInCard = true,
  inputId = 'match-duration',
}: MatchDurationFieldProps) {
  const config = { ...DEFAULT_TOURNAMENT_CONFIG_FIELD_CONFIG, ...userConfig };

  const min = config.matchDuration?.min ?? 30;
  const max = config.matchDuration?.max ?? 120;
  const step = config.matchDuration?.step ?? 15;

  const handleChange = (next: number) => {
    const clamped = Math.max(min, Math.min(next, max));
    onChange(clamped);
  };

  const field = (
    <div className="space-y-4">
      <div className="space-y-2" data-guide-id="wizard.config.matchDuration">
        <HelpLabel htmlFor={inputId} topicId="wizard.config.matchDuration">
          Duración estimada por partido (minutos) *
        </HelpLabel>
        <div className="flex items-center gap-4">
          <Input
            id={inputId}
            type="number"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) =>
              handleChange(parseInt(e.target.value, 10) || min)
            }
            className="w-24 text-center text-lg font-medium"
          />
          <span className="text-gray-500">minutos</span>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-sm text-gray-500">Duraciones comunes:</Label>
        <div className="flex flex-wrap gap-2">
          {presets.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => handleChange(preset)}
              className={cn(
                'rounded-full px-4 py-2 text-sm font-medium transition-colors',
                value === preset
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
              )}
            >
              {preset} min
            </button>
          ))}
        </div>
      </div>

      <p className="text-sm text-gray-500">
        Esta duración se usará para calcular la cantidad de partidos que pueden
        jugarse en los horarios seleccionados.
      </p>
    </div>
  );

  if (!wrapInCard) {
    return <div className={wizardBlockShellClassName}>{field}</div>;
  }

  return (
    <Card className={wizardBlockCardClassName}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Clock className="h-5 w-5 text-blue-600" />
          Duración de partidos
        </CardTitle>
      </CardHeader>
      <CardContent>{field}</CardContent>
    </Card>
  );
}
